<template>
  <div id="app" class="flex-1 overflow-visible max-h-full">
    <header-component v-if="requiresAuth"/>
    <div :class="shouldAddPadding">
      <header v-if="requiresAuth"/>
      <main>
        <div :class="isDashboard">
          <router-view/>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import HeaderComponent from "@/components/headerComponent";

export default {
  name: 'App',
  components: {
    HeaderComponent,
    },
  computed: {
    ...mapGetters(["getTheme"]),
    requiresAuth() {
      return this.$route.meta.requiresAuth;
    },
    shouldAddPadding() {
      return this.$route.meta.requiresAuth ? "py-10" : "";
    },
    isDashboard() {
      return this.$route.meta.requiresAuth ? "max-w-7xl mx-auto sm:px-6 lg:px-8" : "";
    }
  },
  created() {
    document.documentElement.setAttribute('data-theme', this.getTheme);
  },
  mounted() {
    this.setIsFromPortal();
  },
  methods: {
    ...mapMutations(["setIsFromPortal"]),
  },
  watch: {
    getTheme(val) {
      document.documentElement.setAttribute('data-theme', val);
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

/* ── Global reset ─────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

/* ── CSS custom properties — DARK (default) ─────── */
:root {
  /* Page / surfaces */
  --cc-page:          #050a10;
  --cc-card:          rgba(5, 10, 16, 0.80);
  --cc-card-border:   rgba(255, 255, 255, 0.07);
  --cc-card-shadow:   0 40px 80px rgba(0,0,0,0.65), 0 0 0 1px rgba(0,146,191,0.14), inset 0 1px 0 rgba(255,255,255,0.04);
  --cc-card-foot:     rgba(255, 255, 255, 0.055);
  --cc-overlay-grad:  linear-gradient(120deg, rgba(5,10,16,0.94) 0%, rgba(24,43,97,0.70) 48%, rgba(5,10,16,0.52) 100%);

  /* Inputs */
  --cc-input:         rgba(255, 255, 255, 0.045);
  --cc-input-border:  rgba(255, 255, 255, 0.09);
  --cc-input-text:    #ffffff;
  --cc-placeholder:   rgba(255, 255, 255, 0.18);
  --cc-eye-bg:        rgba(255, 255, 255, 0.045);
  --cc-eye-border:    rgba(255, 255, 255, 0.09);
  --cc-eye-icon:      rgba(255, 255, 255, 0.35);
  --cc-autofill-bg:   #080e18;

  /* Text scale */
  --cc-t1:  #ffffff;                      /* headings */
  --cc-t2:  rgba(255, 255, 255, 0.55);   /* secondary */
  --cc-t3:  rgba(255, 255, 255, 0.38);   /* sub / body */
  --cc-t4:  rgba(255, 255, 255, 0.40);   /* labels */
  --cc-t5:  rgba(255, 255, 255, 0.13);   /* wordmark */
  --cc-t6:  rgba(255, 255, 255, 0.82);   /* values */
  --cc-t7:  rgba(255, 255, 255, 0.25);   /* support link */

  /* Navigation */
  --cc-nav:             rgba(5, 10, 16, 0.92);
  --cc-nav-border:      rgba(255, 255, 255, 0.07);
  --cc-nav-name:        rgba(255, 255, 255, 0.75);
  --cc-nav-chevron:     rgba(255, 255, 255, 0.35);
  --cc-dropdown:        rgba(8, 14, 24, 0.97);
  --cc-dropdown-border: rgba(255, 255, 255, 0.08);
  --cc-dropdown-shadow: 0 20px 48px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,146,191,0.10);
  --cc-dropdown-item:   rgba(255, 255, 255, 0.55);
  --cc-mobile-btn:      rgba(255, 255, 255, 0.55);
  --cc-panel:           rgba(5, 10, 16, 0.97);
  --cc-panel-sep:       rgba(255, 255, 255, 0.06);
  --cc-panel-link:      rgba(255, 255, 255, 0.55);
  --cc-panel-uname:     rgba(255, 255, 255, 0.90);
  --cc-panel-email:     rgba(255, 255, 255, 0.38);
  --cc-panel-ulink:     rgba(255, 255, 255, 0.45);

  /* Modal */
  --cc-modal:         rgba(8, 14, 24, 0.97);
  --cc-modal-border:  rgba(255, 255, 255, 0.08);
  --cc-modal-shadow:  0 40px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,146,191,0.12);
  --cc-backdrop:      rgba(0, 0, 0, 0.72);

  /* Dividers */
  --cc-sep:        rgba(255, 255, 255, 0.07);
  --cc-sep-subtle: rgba(255, 255, 255, 0.05);

  /* Warning (no-tenants) */
  --cc-warn-border: #fcef60;
  --cc-warn-bg:     rgba(252, 239, 96, 0.07);
  --cc-warn-title:  #fcef60;
  --cc-warn-body:   rgba(252, 239, 96, 0.60);

  /* Tenant card */
  --cc-tc-name:    #ffffff;
  --cc-tc-chevron: rgba(255, 255, 255, 0.22);

  /* Toggle button */
  --cc-toggle-bg:    rgba(255, 255, 255, 0.07);
  --cc-toggle-icon:  rgba(255, 255, 255, 0.55);
  --cc-toggle-hover: rgba(255, 255, 255, 0.12);
}

/* ── CSS custom properties — LIGHT override ─────── */
[data-theme="light"] {
  --cc-page:          #edf2f7;
  --cc-card:          rgba(255, 255, 255, 0.95);
  --cc-card-border:   rgba(0, 0, 0, 0.10);
  --cc-card-shadow:   0 20px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,146,191,0.14), inset 0 1px 0 rgba(255,255,255,0.80);
  --cc-card-foot:     rgba(0, 0, 0, 0.08);
  --cc-overlay-grad:  linear-gradient(120deg, rgba(237,242,247,0.60) 0%, rgba(200,215,240,0.35) 48%, rgba(237,242,247,0.40) 100%);

  --cc-input:         rgba(0, 0, 0, 0.04);
  --cc-input-border:  rgba(0, 0, 0, 0.12);
  --cc-input-text:    #0a0f18;
  --cc-placeholder:   rgba(10, 15, 24, 0.28);
  --cc-eye-bg:        rgba(0, 0, 0, 0.04);
  --cc-eye-border:    rgba(0, 0, 0, 0.12);
  --cc-eye-icon:      rgba(10, 15, 24, 0.40);
  --cc-autofill-bg:   #f5f8fc;

  --cc-t1:  #0a0f18;
  --cc-t2:  rgba(10, 15, 24, 0.60);
  --cc-t3:  rgba(10, 15, 24, 0.50);
  --cc-t4:  rgba(10, 15, 24, 0.55);
  --cc-t5:  rgba(10, 15, 24, 0.18);
  --cc-t6:  rgba(10, 15, 24, 0.82);
  --cc-t7:  rgba(10, 15, 24, 0.40);

  --cc-nav:             rgba(255, 255, 255, 0.95);
  --cc-nav-border:      rgba(0, 0, 0, 0.09);
  --cc-nav-name:        rgba(10, 15, 24, 0.80);
  --cc-nav-chevron:     rgba(10, 15, 24, 0.35);
  --cc-dropdown:        rgba(255, 255, 255, 0.99);
  --cc-dropdown-border: rgba(0, 0, 0, 0.10);
  --cc-dropdown-shadow: 0 20px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,146,191,0.12);
  --cc-dropdown-item:   rgba(10, 15, 24, 0.60);
  --cc-mobile-btn:      rgba(10, 15, 24, 0.55);
  --cc-panel:           rgba(245, 248, 252, 0.99);
  --cc-panel-sep:       rgba(0, 0, 0, 0.08);
  --cc-panel-link:      rgba(10, 15, 24, 0.55);
  --cc-panel-uname:     rgba(10, 15, 24, 0.90);
  --cc-panel-email:     rgba(10, 15, 24, 0.45);
  --cc-panel-ulink:     rgba(10, 15, 24, 0.50);

  --cc-modal:         rgba(255, 255, 255, 0.99);
  --cc-modal-border:  rgba(0, 0, 0, 0.10);
  --cc-modal-shadow:  0 20px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,146,191,0.14);
  --cc-backdrop:      rgba(0, 0, 0, 0.45);

  --cc-sep:        rgba(0, 0, 0, 0.09);
  --cc-sep-subtle: rgba(0, 0, 0, 0.07);

  --cc-warn-border: #d97706;
  --cc-warn-bg:     rgba(217, 119, 6, 0.08);
  --cc-warn-title:  #b45309;
  --cc-warn-body:   rgba(120, 60, 0, 0.70);

  --cc-tc-name:    #0a0f18;
  --cc-tc-chevron: rgba(10, 15, 24, 0.30);

  --cc-toggle-bg:    rgba(0, 0, 0, 0.06);
  --cc-toggle-icon:  rgba(10, 15, 24, 0.55);
  --cc-toggle-hover: rgba(0, 0, 0, 0.10);
}

/* ── Base ─────────────────────────────────────────── */
html, body {
  margin: 0;
  padding: 0;
  background-color: var(--cc-page);
  font-family: 'Barlow', sans-serif;
  color: var(--cc-t1);
  min-height: 100vh;
  transition: background-color 0.3s ease, color 0.3s ease;
}

#app {
  background-color: transparent;
  min-height: 100vh;
}
</style>